import { useState } from "react";
import { IconBan } from "@tabler/icons-react";
import { Badge, Checkbox, EditRowActions, Input, Select, Td, Text, Tr, useConfirmDialog } from "@/components/ui";
import type { CuentaContable } from "@/lib/types";
import type { MapeoClaveCuenta } from "../services/mapeos-clave-cuenta-service";
import { useCambiarEstadoMapeoClaveCuenta, useEditarMapeoClaveCuenta } from "../hooks/use-mapeos-clave-cuenta-mutations";

/**
 * Fila del catálogo · alterna vista/edición inline (clave SAT, cuenta contable
 * y nombre). Baja lógica en vez de eliminar: los prorrateos históricos ya
 * derivaron su cuenta de este mapeo.
 */
export function MapeoRow({
  mapeo,
  cuentas,
  seleccionada,
  onToggleSeleccion,
}: {
  mapeo: MapeoClaveCuenta;
  cuentas: CuentaContable[];
  seleccionada: boolean;
  onToggleSeleccion: () => void;
}) {
  const [editando, setEditando] = useState(false);
  const [claveSat, setClaveSat] = useState(mapeo.claveSat);
  const [cuentaContableId, setCuentaContableId] = useState(mapeo.cuentaContableId);
  const [nombre, setNombre] = useState(mapeo.nombre);
  const confirm = useConfirmDialog();

  const editar = useEditarMapeoClaveCuenta({ onEditado: () => setEditando(false) });
  const cambiarEstado = useCambiarEstadoMapeoClaveCuenta();

  const cancelar = () => {
    setClaveSat(mapeo.claveSat);
    setCuentaContableId(mapeo.cuentaContableId);
    setNombre(mapeo.nombre);
    setEditando(false);
  };

  const guardar = () =>
    editar.mutate({ id: mapeo.id, input: { claveSat: claveSat.trim(), cuentaContableId, nombre: nombre.trim() } });

  const confirmarBaja = async () => {
    const ok = await confirm({
      title: `Dar de baja "${mapeo.claveSat}"`,
      message:
        "La clave deja de derivar cuenta contable en los nuevos prorrateos. Los renglones históricos que ya la usan conservan su cuenta. Puedes reactivarla después.",
      confirmLabel: "Dar de baja",
      confirmColor: "peligro",
      icon: <IconBan size={14} />,
    });
    if (ok) cambiarEstado.mutate({ id: mapeo.id, claveSat: mapeo.claveSat, activo: false });
  };

  if (editando) {
    return (
      <Tr className="bg-muted/50">
        <Td />
        <Td>
          <Input size="xs" value={claveSat} onChange={(e) => setClaveSat(e.currentTarget.value.toUpperCase())} className="w-28 font-mono" autoFocus />
        </Td>
        <Td>
          <Text size="sm" c="dimmed">
            {mapeo.claveSatDescripcion ?? "—"}
          </Text>
        </Td>
        <Td>
          <Select
            size="xs"
            options={cuentas.map((c) => ({ value: c.id, label: `${c.codigo} · ${c.nombre}` }))}
            value={cuentaContableId}
            onValueChange={setCuentaContableId}
            searchable
            searchPlaceholder="Buscar cuenta…"
          />
        </Td>
        <Td>
          <Input size="xs" value={nombre} onChange={(e) => setNombre(e.currentTarget.value)} />
        </Td>
        <Td>
          <BadgeEstado activo={mapeo.activo} />
        </Td>
        <Td align="right">
          <EditRowActions mode="edit" saving={editar.isPending} onCancel={cancelar} onSave={guardar} />
        </Td>
      </Tr>
    );
  }

  return (
    <Tr>
      <Td>
        <Checkbox
          checked={seleccionada}
          onCheckedChange={() => onToggleSeleccion()}
          aria-label={`Seleccionar ${mapeo.claveSat}`}
        />
      </Td>
      <Td>
        <Text fw={600} size="sm" ff="mono">
          {mapeo.claveSat}
        </Text>
      </Td>
      <Td>
        <Text size="sm" c="dimmed">
          {mapeo.claveSatDescripcion ?? "—"}
        </Text>
      </Td>
      <Td>
        <Text size="sm">
          <span className="font-mono">{mapeo.cuentaCodigo}</span> · {mapeo.cuentaNombre}
        </Text>
      </Td>
      <Td>
        <Text size="sm">{mapeo.nombre || "—"}</Text>
      </Td>
      <Td>
        <BadgeEstado activo={mapeo.activo} />
      </Td>
      <Td align="right">
        <EditRowActions
          mode="view"
          onEdit={() => setEditando(true)}
          onDelete={mapeo.activo ? confirmarBaja : undefined}
          deleteVariant="baja"
          deleting={cambiarEstado.isPending}
          reactivable={!mapeo.activo}
          onReactivar={() => cambiarEstado.mutate({ id: mapeo.id, claveSat: mapeo.claveSat, activo: true })}
          reactivando={cambiarEstado.isPending}
        />
      </Td>
    </Tr>
  );
}

function BadgeEstado({ activo }: { activo: boolean }) {
  return (
    <Badge size="sm" color={activo ? "exito" : "slate"}>
      {activo ? "Activo" : "Inactivo"}
    </Badge>
  );
}
